import { useCallback } from 'react';
import { toast } from 'react-toastify';
import useHttp from './useHttp';
import { useAuth } from '../context/authContext';

export default function usePendingBookings() {
  const { sendRequest } = useHttp();
  // NOTE: we intentionally do NOT depend on the whole `authCtx` object here.
  // AuthContext re-memoizes its value whenever things like sessionTimeRemaining
  // or storedRequests tick, which gave every callback below a fresh identity
  // on every auth re-render. Downstream useEffects (e.g. the resolution-
  // candidates loader in the dialogs) depended on these callbacks and ended
  // up re-fetching in a tight loop. Pulling out only the stable useCallback
  // `getAuthHeaders` breaks that chain.
  const { getAuthHeaders } = useAuth();

  const fetchPendingBookings = useCallback(
    async ({ entertainmentId, showAll = false } = {}) => {
      try {
        const params = {};
        if (entertainmentId) params.entertainmentId = entertainmentId;
        if (showAll) params.showAll = 'true';

        const res = await sendRequest({
          url: '/api/v1/internalBooking/pending',
          method: 'GET',
          headers: getAuthHeaders(),
          params: Object.keys(params).length > 0 ? params : undefined,
        });
        return {
          results: res?.results || [],
          sections: res?.sections || [],
        };
      } catch (error) {
        toast.error('Fehler beim Laden des operativen Handlungsbedarfs');
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const confirmPendingBooking = useCallback(
    async (
      bookingId,
      toastMessage = null,
      onStored = null,
      displayInfo = null,
      onSuccess = null,
      successToastMessage = null
    ) => {
      try {
        const res = await sendRequest(
          {
            url: `/api/v1/internalBooking/${bookingId}/confirmPending`,
            method: 'POST',
            headers: getAuthHeaders(),
          },
          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );
        const booking = res?.booking || null;
        if (booking && onSuccess) onSuccess(booking);
        return booking;
      } catch (error) {
        if (error.message !== 'INSUFFICIENT_RIGHTS') {
          toast.error('Fehler beim Bestätigen der Buchungsanfrage');
        }
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const rejectPendingBooking = useCallback(
    async (
      bookingId,
      toastMessage = null,
      onStored = null,
      displayInfo = null,
      onSuccess = null,
      successToastMessage = null
    ) => {
      try {
        const res = await sendRequest(
          {
            url: `/api/v1/internalBooking/${bookingId}/rejectPending`,
            method: 'POST',
            headers: getAuthHeaders(),
          },
          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );
        const booking = res?.booking || null;
        if (booking && onSuccess) onSuccess(booking);
        return booking;
      } catch (error) {
        if (error.message !== 'INSUFFICIENT_RIGHTS') {
          toast.error('Fehler beim Ablehnen der Buchungsanfrage');
        }
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const retroactiveComplete = useCallback(
    async (
      bookingId,
      {
        markedPaid,
        reason = '',
        responsibleUserId = null,
      } = {},
      toastMessage = null,
      onStored = null,
      displayInfo = null,
      onSuccess = null,
      successToastMessage = null
    ) => {
      try {
        const res = await sendRequest(
          {
            url: `/api/v1/internalBooking/${bookingId}/retroactiveComplete`,
            method: 'POST',
            headers: getAuthHeaders(),
            body: {
              markedPaid: Boolean(markedPaid),
              reason,
              responsibleUserId: responsibleUserId || null,
            },
            requiredRights: ['internalBooking:special:resolveForgotten'],
          },
          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );
        const booking = res?.booking || null;
        if (booking && onSuccess) onSuccess(booking);
        return booking;
      } catch (error) {
        if (error.message !== 'INSUFFICIENT_RIGHTS') {
          toast.error('Fehler beim nachträglichen Abschließen der Buchung');
        }
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const resolveForgottenPayment = useCallback(
    async (
      bookingId,
      { reason = '', responsibleUserId = null } = {},
      toastMessage = null,
      onStored = null,
      displayInfo = null,
      onSuccess = null,
      successToastMessage = null
    ) => {
      try {
        const res = await sendRequest(
          {
            url: `/api/v1/internalBooking/${bookingId}/resolveForgottenPayment`,
            method: 'POST',
            headers: getAuthHeaders(),
            body: { reason, responsibleUserId: responsibleUserId || null },
            requiredRights: ['internalBooking:special:resolveForgotten'],
          },
          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );
        const booking = res?.booking || null;
        if (booking && onSuccess) onSuccess(booking);
        return booking;
      } catch (error) {
        if (error.message !== 'INSUFFICIENT_RIGHTS') {
          toast.error('Fehler beim Auflösen der vergessenen Zahlung');
        }
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const acknowledgeAutoCheckOutBulk = useCallback(
    async (
      bookingIds,
      toastMessage = null,
      onStored = null,
      displayInfo = null,
      onSuccess = null,
      successToastMessage = null
    ) => {
      try {
        const res = await sendRequest(
          {
            url: `/api/v1/internalBooking/acknowledgeAutoCheckOutBulk`,
            method: 'POST',
            headers: getAuthHeaders(),
            body: { bookingIds },
            requiredRights: ['internalBooking:special:resolveForgotten'],
          },
          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );
        const summary = {
          resolved: Number.isFinite(res?.resolved) ? res.resolved : 0,
          skipped: Number.isFinite(res?.skipped) ? res.skipped : 0,
          failed: Number.isFinite(res?.failed) ? res.failed : 0,
          results: Array.isArray(res?.results) ? res.results : [],
        };
        if (onSuccess) onSuccess(summary);
        return summary;
      } catch (error) {
        if (error.message !== 'INSUFFICIENT_RIGHTS') {
          toast.error('Fehler beim Bestätigen der automatischen Checkouts');
        }
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const fetchResolutionCandidates = useCallback(
    async bookingId => {
      try {
        const res = await sendRequest({
          url: `/api/v1/internalBooking/${bookingId}/resolutionCandidates`,
          method: 'GET',
          headers: getAuthHeaders(),
        });
        return {
          resolutionType: res?.resolutionType || null,
          candidates: Array.isArray(res?.candidates) ? res.candidates : [],
        };
      } catch (error) {
        if (error.message !== 'INSUFFICIENT_RIGHTS') {
          toast.error('Fehler beim Laden der Verantwortlichen');
        }
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  // Mark a booking's customer hint as read so it leaves the "Kundenhinweise"
  // attention section. Pass handled=false to undo. The hint text itself is preserved.
  const markCustomerCommentHandled = useCallback(
    async (bookingId, handled = true) => {
      try {
        const res = await sendRequest({
          url: `/api/v1/internalBooking/${bookingId}/customer-comment/handled`,
          method: 'POST',
          headers: getAuthHeaders(),
          body: { handled },
          requiredRights: ['internalBooking:special:handleCustomerComments'],
        });
        return res?.data || null;
      } catch (error) {
        if (error.message !== 'INSUFFICIENT_RIGHTS') {
          toast.error('Fehler beim Markieren des Kundenhinweises');
        }
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  // Mark a failed refund as read so it leaves the "Fehlgeschlagene Rückerstattungen"
  // attention section. Pass acknowledged=false to undo. The refund record is preserved.
  const acknowledgeFailedRefund = useCallback(
    async (paymentId, refundId, acknowledged = true) => {
      try {
        const res = await sendRequest({
          url: `/api/v1/payment/${paymentId}/refunds/${refundId}/acknowledge`,
          method: 'POST',
          headers: getAuthHeaders(),
          body: { acknowledged },
          requiredRights: ['payment:special:acknowledgeFailedRefunds'],
        });
        return res?.data || null;
      } catch (error) {
        if (error.message !== 'INSUFFICIENT_RIGHTS') {
          toast.error('Fehler beim Markieren der Rückerstattung');
        }
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  return {
    fetchPendingBookings,
    confirmPendingBooking,
    rejectPendingBooking,
    retroactiveComplete,
    resolveForgottenPayment,
    acknowledgeAutoCheckOutBulk,
    fetchResolutionCandidates,
    markCustomerCommentHandled,
    acknowledgeFailedRefund,
  };
}
